import { useMemo, useState } from 'react'

import { Button, Card, Checkbox } from '@/components/ui'
import { Icon } from '@/common/components/Icon'
import { IColumn, Table } from '@/core/components/Table'
import {
  BadgeDto,
  RoadmapDto,
  StepDto,
  StepUpsertDto,
  narrowStepTarget,
} from '@/common/models/gamification'
import { deleteStep, updateStep } from '@/core/services/gamification'

import { DeleteConfirmationModal } from '../../DeleteConfirmationModal'
import { translationTitle } from '../shared/labels'
import { StepEditorModal } from './StepEditorModal'

interface StepListProps {
  roadmap: RoadmapDto
  steps: StepDto[]
  allRoadmaps: RoadmapDto[]
  badges: BadgeDto[]
  hasEditPermission: boolean
  onChanged: () => Promise<void> | void
  onError: (e: unknown) => void
}

/** Rebuild a full upsert body from an existing step (PUT overwrites all fields). */
const toUpsert = (step: StepDto, ordinal: number): StepUpsertDto => ({
  ordinal,
  unlockMode: step.unlockMode,
  targetType: step.targetType ?? null,
  targetId: step.targetId ?? null,
  badgeId: step.badgeId ?? null,
  iconRef: step.iconRef ?? null,
  active: step.active,
  hidden: step.hidden,
})

const targetSummary = (step: StepDto): string => {
  const narrowed = narrowStepTarget(step)
  if (!narrowed.targetType) return '—'
  const title =
    narrowed.target && 'translations' in narrowed.target
      ? translationTitle(
          (
            narrowed.target as {
              translations?: { locale: string; title?: string }[]
            }
          ).translations,
          '',
        )
      : ''
  const label = narrowed.targetType.replace('_', ' ').toLowerCase()
  return title ? `${label}: ${title}` : label
}

export const StepList = ({
  roadmap,
  steps,
  allRoadmaps,
  badges,
  hasEditPermission,
  onChanged,
  onError,
}: StepListProps) => {
  const [editorOpen, setEditorOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<StepDto | null>(null)

  // Render in ordinal order so row position matches each step's ordinal.
  const orderedSteps = useMemo(
    () => [...steps].sort((a, b) => a.ordinal - b.ordinal),
    [steps],
  )

  // Derive the step from the latest list so dependency edits reflect live.
  const editingStep = editingId
    ? steps.find((s) => s.id === editingId) ?? null
    : null

  const badgeName = (badgeId?: string | null) =>
    badges.find((b) => b.id === badgeId)?.name ?? '—'

  const openCreate = () => {
    setEditingId(null)
    setEditorOpen(true)
  }

  const openEdit = (step: StepDto) => {
    if (!hasEditPermission) return
    setEditingId(step.id)
    setEditorOpen(true)
  }

  const handleDelete = async (step: StepDto) => {
    try {
      await deleteStep(step.id)
      await onChanged()
    } catch (e) {
      onError(e)
    }
  }

  const handleReorder = async (next: StepDto[]) => {
    try {
      await Promise.all(
        next.map((step, index) =>
          step.ordinal === index
            ? Promise.resolve()
            : updateStep(step.id, toUpsert(step, index)),
        ),
      )
      await onChanged()
    } catch (e) {
      onError(e)
    }
  }

  const columns: IColumn[] = useMemo(
    () => [
      {
        title: 'Order',
        accessor: 'ordinal',
        render: (row: StepDto) => (
          <span className="text-monochrome-2">{row.ordinal + 1}</span>
        ),
      },
      {
        title: 'Icon',
        accessor: 'iconRef',
        render: (row: StepDto) =>
          row.iconRef ? (
            <img
              src={row.iconRef}
              alt=""
              width={42}
              height={42}
              className="rounded-[8px] border border-monochrome-120 object-contain"
              style={{ width: 42, height: 42 }}
            />
          ) : (
            <span className="text-monochrome-2">—</span>
          ),
      },
      {
        title: 'Target',
        accessor: 'target',
        render: (row: StepDto) => (
          <span className="text-monochrome-1 capitalize">
            {targetSummary(row)}
          </span>
        ),
      },
      {
        title: 'Unlock',
        accessor: 'unlockMode',
        render: (row: StepDto) => (
          <span className="text-monochrome-2">{row.unlockMode}</span>
        ),
      },
      {
        title: 'Deps',
        accessor: 'dependencies',
        render: (row: StepDto) => (
          <span className="text-monochrome-2">
            {row.dependencies?.length ?? 0}
          </span>
        ),
      },
      {
        title: 'Badge',
        accessor: 'badgeId',
        render: (row: StepDto) => (
          <span className="text-monochrome-2">{badgeName(row.badgeId)}</span>
        ),
      },
      {
        title: 'Active',
        accessor: 'active',
        render: (row: StepDto) => <Checkbox checked={row.active} disabled />,
      },
      {
        title: 'Hidden',
        accessor: 'hidden',
        render: (row: StepDto) => <Checkbox checked={row.hidden} disabled />,
      },
      {
        title: '',
        accessor: '',
        render: (row: StepDto) => (
          <div className="flex items-center gap-4">
            <a
              className="text-accent-1 underline"
              href="#"
              onClick={(e) => {
                e.preventDefault()
                openEdit(row)
              }}
            >
              Edit
            </a>
            {hasEditPermission && (
              <a
                className="text-accent-3 underline"
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  setDeleting(row)
                }}
              >
                Delete
              </a>
            )}
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasEditPermission, badges],
  )

  return (
    <Card className="p-5 !bg-monochrome-4-60">
      <div className="mb-4 flex items-center justify-between">
        <div className="text-2xl font-bold text-monochrome-1">Steps</div>
        {hasEditPermission && (
          <Button variant="primary-bordered" onClick={openCreate}>
            Add Step
            <Icon
              icon="plus-circle"
              width="18"
              height="18"
              viewBox="0 0 18 18"
            />
          </Button>
        )}
      </div>

      <Table
        columns={columns}
        data={orderedSteps}
        rowReorder={hasEditPermission}
        getRowId={(row: StepDto) => row.id}
        onRowReorder={handleReorder}
        noDataMessage="No steps yet"
      />

      <StepEditorModal
        open={editorOpen}
        onOpenChange={setEditorOpen}
        roadmapId={roadmap.id}
        editionId={roadmap.festivalEditionId}
        step={editingStep}
        allSteps={orderedSteps}
        allRoadmaps={allRoadmaps}
        badges={badges}
        onChanged={onChanged}
        onError={onError}
      />

      <DeleteConfirmationModal
        showDeleteConfirmationModal={!!deleting}
        setDeleteConfirmationModal={(show) => {
          if (!show) setDeleting(null)
        }}
        deletedItem={
          deleting
            ? `step ${deleting.ordinal + 1}${
                deleting.targetType === 'QUEST' ||
                deleting.targetType === 'QUEST_GROUP'
                  ? ' and its quest/quest group'
                  : ''
              }`
            : undefined
        }
        deleteFunc={() => {
          if (deleting) handleDelete(deleting)
          setDeleting(null)
        }}
      />
    </Card>
  )
}
